import { useEffect, useState } from 'react';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import type { FilterFieldConfig } from '../../config/filterable-fields';
import {
  FilterRowBuilder,
  type FilterMatchType,
  type FilterRow,
} from '../filter-row-builder/filter-row-builder';

export type AdvancedFiltersDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: FilterRow[];
  matchType: FilterMatchType;
  onApply: (rows: FilterRow[], matchType: FilterMatchType) => void;
  /** Forwarded to FilterRowBuilder — defaults to the full FILTERABLE_FIELDS registry when absent. */
  registry?: FilterFieldConfig[];
};

const emptyRow = (): FilterRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  fieldKey: '',
  operator: '',
  value: '',
});

export const AdvancedFiltersDialog = ({
  open,
  onOpenChange,
  rows,
  matchType,
  onApply,
  registry,
}: AdvancedFiltersDialogProps) => {
  const [draftRows, setDraftRows] = useState<FilterRow[]>(rows);
  const [draftMatchType, setDraftMatchType] = useState<FilterMatchType>(matchType);

  useEffect(() => {
    if (!open) return;
    setDraftRows(rows);
    setDraftMatchType(matchType);
  }, [open, rows, matchType]);

  const handleReset = () => {
    setDraftRows([emptyRow()]);
    setDraftMatchType('all');
  };

  const handleApply = () => {
    onApply(draftRows, draftMatchType);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Advanced Filters</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <FilterRowBuilder
            rows={draftRows}
            onChange={setDraftRows}
            matchType={draftMatchType}
            onMatchTypeChange={setDraftMatchType}
            registry={registry}
          />
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button type="button" variant="ghost" onClick={handleReset}>
            Reset
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-[#0161AB] hover:bg-[#01558F] text-white"
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
